import { tryGetUpnOverride } from '../utils/upnOverrideUtils';

/** Per-tab OBO session (sessionStorage). */
const ON_BEHALF_OF_UPN_STORAGE_KEY = 'onBehalfOfUpn';
const ON_BEHALF_OF_ENABLED_STORAGE_KEY = 'onBehalfOfEnabled';
const ON_BEHALF_OF_CONFIRMED_UPN_SESSION_KEY = 'onBehalfOfConfirmedUpn';
const ON_BEHALF_OF_PENDING_UPN_SESSION_KEY = 'onBehalfOfPendingUpn';
/** Set on first run so we migrate legacy localStorage enabled/upn at most once per tab. */
const ON_BEHALF_OF_SESSION_INIT_MARKER_KEY = 'onBehalfOfSessionInit';

/** Shared across tabs. */
const ON_BEHALF_OF_RECENT_UPNS_STORAGE_KEY = 'onBehalfOfRecentUpns';
const ON_BEHALF_OF_RECENT_UPNS_MAX = 20;

function sessionGet(key: string): string | null {
    try {
        return sessionStorage.getItem(key);
    } catch {
        return null;
    }
}

function sessionSet(key: string, value: string): void {
    try {
        sessionStorage.setItem(key, value);
    } catch {
        // ignore
    }
}

function sessionRemove(key: string): void {
    try {
        sessionStorage.removeItem(key);
    } catch {
        // ignore
    }
}

/** One-time per tab: copy former localStorage OBO keys into sessionStorage (same key names). */
function migrateLegacyLocalOnBehalfOfToSessionOnce(): void {
    try {
        if (sessionGet(ON_BEHALF_OF_SESSION_INIT_MARKER_KEY) === '1') return;
        const legEn = localStorage.getItem(ON_BEHALF_OF_ENABLED_STORAGE_KEY);
        const legUpn = localStorage.getItem(ON_BEHALF_OF_UPN_STORAGE_KEY);
        if (legEn !== null) sessionSet(ON_BEHALF_OF_ENABLED_STORAGE_KEY, legEn);
        if (legUpn !== null) sessionSet(ON_BEHALF_OF_UPN_STORAGE_KEY, legUpn);
        if (legEn !== null) localStorage.removeItem(ON_BEHALF_OF_ENABLED_STORAGE_KEY);
        if (legUpn !== null) localStorage.removeItem(ON_BEHALF_OF_UPN_STORAGE_KEY);
        sessionSet(ON_BEHALF_OF_SESSION_INIT_MARKER_KEY, '1');
    } catch {
        try {
            sessionSet(ON_BEHALF_OF_SESSION_INIT_MARKER_KEY, '1');
        } catch {
            // ignore
        }
    }
}

function persistConfirmedUpnToSession(upn: string | null): void {
    if (upn != null && upn.trim() !== '') sessionSet(ON_BEHALF_OF_CONFIRMED_UPN_SESSION_KEY, upn.trim());
    else sessionRemove(ON_BEHALF_OF_CONFIRMED_UPN_SESSION_KEY);
}

function persistPendingUpnToSession(upn: string | null): void {
    if (upn != null && upn.trim() !== '') sessionSet(ON_BEHALF_OF_PENDING_UPN_SESSION_KEY, upn.trim());
    else sessionRemove(ON_BEHALF_OF_PENDING_UPN_SESSION_KEY);
}

function readConfirmedUpnFromSession(): string | null {
    const v = sessionGet(ON_BEHALF_OF_CONFIRMED_UPN_SESSION_KEY);
    if (v == null || v.trim() === '') return null;
    return v.trim();
}

function readPendingUpnFromSession(): string | null {
    const v = sessionGet(ON_BEHALF_OF_PENDING_UPN_SESSION_KEY);
    if (v == null || v.trim() === '') return null;
    return v.trim();
}

function clearOnBehalfOfSessionKeysExceptInitMarker(): void {
    sessionRemove(ON_BEHALF_OF_ENABLED_STORAGE_KEY);
    sessionRemove(ON_BEHALF_OF_UPN_STORAGE_KEY);
    sessionRemove(ON_BEHALF_OF_CONFIRMED_UPN_SESSION_KEY);
    sessionRemove(ON_BEHALF_OF_PENDING_UPN_SESSION_KEY);
}

export default class ImpersonationService {
    private static impersonateId: string | null = null;

    /** On-behalf-of: enabled only when checkbox is on and UPN is non-empty. */
    private static onBehalfOfEnabled = false;
    private static onBehalfOfUpn = '';
    /** UPN we have confirmed via a successful entities/me; header only sent when this matches current UPN (or pending). */
    private static confirmedUpn: string | null = null;
    /** UPN we are currently trying (so the trial fetchMe gets the header); cleared when fetch completes. */
    private static pendingUpn: string | null = null;
    private static onBehalfOfListeners: Set<() => void> = new Set();
    private static recentUpnsListeners: Set<() => void> = new Set();

    /** Read persisted on-behalf-of UPN from this tab's sessionStorage (after legacy localStorage migration). */
    public static getPersistedOnBehalfOfUpn(): string {
        migrateLegacyLocalOnBehalfOfToSessionOnce();
        const v = sessionGet(ON_BEHALF_OF_UPN_STORAGE_KEY);
        return v != null ? String(v).trim() : '';
    }

    /** Read persisted on-behalf-of checkbox from this tab's sessionStorage. */
    public static getPersistedOnBehalfOfEnabled(): boolean {
        migrateLegacyLocalOnBehalfOfToSessionOnce();
        return sessionGet(ON_BEHALF_OF_ENABLED_STORAGE_KEY) === 'true';
    }

    /**
     * Load enabled / UPN / confirmed / pending from sessionStorage into in-memory state.
     * Runs once at module load; safe to call again (e.g. after restoring a backup session).
     */
    public static rehydrateFromSessionStorage(): void {
        migrateLegacyLocalOnBehalfOfToSessionOnce();
        const enabled = sessionGet(ON_BEHALF_OF_ENABLED_STORAGE_KEY) === 'true';
        const upn = (sessionGet(ON_BEHALF_OF_UPN_STORAGE_KEY) ?? '').trim();
        let confirmed = readConfirmedUpnFromSession();
        let pending = readPendingUpnFromSession();
        if (confirmed != null && confirmed !== upn) {
            confirmed = null;
            persistConfirmedUpnToSession(null);
        }
        if (pending != null && pending !== upn) {
            pending = null;
            persistPendingUpnToSession(null);
        }
        ImpersonationService.onBehalfOfEnabled = enabled;
        ImpersonationService.onBehalfOfUpn = upn;
        ImpersonationService.confirmedUpn = confirmed;
        ImpersonationService.pendingUpn = pending;
    }

    public static setImpersonation(id: string | null) {
        ImpersonationService.impersonateId = id && id.trim() !== '' ? id : null;
    }

    public static getImpersonation(): string | null {
        return ImpersonationService.impersonateId;
    }

    /**
     * Get the effective impersonation ID, checking both explicit impersonation
     * and dev UPN override (see tryGetUpnOverride).
     */
    public static getEffectiveImpersonation(): string | null {
        // First check explicit impersonation
        if (ImpersonationService.impersonateId) {
            return ImpersonationService.impersonateId;
        }

        // Then check UPN override
        const upnOverride = tryGetUpnOverride();
        return upnOverride || null;
    }

    public static getImpersonationHeader(): Record<string, string> {
        const id = ImpersonationService.getEffectiveImpersonation();
        return id ? { 'X-Impersonate': id } : {};
    }

    // --- On-behalf-of (second impersonation type: x-on-behalf-of with UPN) ---

    public static getOnBehalfOfEnabled(): boolean {
        return ImpersonationService.onBehalfOfEnabled;
    }

    public static getOnBehalfOfUpn(): string {
        return ImpersonationService.onBehalfOfUpn;
    }

    /** UPN we have validated via a successful entities/me; null until then or after clear. */
    public static getOnBehalfOfConfirmedUpn(): string | null {
        return ImpersonationService.confirmedUpn;
    }

    /** True when on-behalf-of is enabled, UPN is set, and we have confirmed that UPN (entities/me succeeded). */
    public static getIsOnBehalfOfEffective(): boolean {
        const upn = ImpersonationService.onBehalfOfUpn.trim();
        return ImpersonationService.onBehalfOfEnabled && upn !== '' && ImpersonationService.confirmedUpn === upn;
    }

    /**
     * Set on-behalf-of state. Header is only sent when enabled and UPN is non-empty.
     * Checkbox and UPN are persisted to sessionStorage (per tab). Recent UPNs remain in localStorage.
     * @param enabled - whether the checkbox is checked
     * @param upn - optional; when provided, updates the UPN and persists it to sessionStorage
     */
    public static setOnBehalfOf(enabled: boolean, upn?: string): void {
        ImpersonationService.onBehalfOfEnabled = enabled;
        if (!enabled) {
            ImpersonationService.confirmedUpn = null;
            ImpersonationService.pendingUpn = null;
            persistConfirmedUpnToSession(null);
            persistPendingUpnToSession(null);
        }
        sessionSet(ON_BEHALF_OF_ENABLED_STORAGE_KEY, String(enabled));
        if (upn !== undefined) {
            const trimmed = (upn ?? '').trim();
            if (trimmed !== ImpersonationService.onBehalfOfUpn) {
                ImpersonationService.confirmedUpn = null;
                ImpersonationService.pendingUpn = null;
                persistConfirmedUpnToSession(null);
                persistPendingUpnToSession(null);
            }
            ImpersonationService.onBehalfOfUpn = trimmed;
            if (trimmed === '') {
                sessionRemove(ON_BEHALF_OF_UPN_STORAGE_KEY);
            } else {
                sessionSet(ON_BEHALF_OF_UPN_STORAGE_KEY, trimmed);
            }
        }
        const u = ImpersonationService.onBehalfOfUpn.trim();
        if (ImpersonationService.confirmedUpn != null && ImpersonationService.confirmedUpn !== u) {
            ImpersonationService.confirmedUpn = null;
            persistConfirmedUpnToSession(null);
        }
        if (ImpersonationService.pendingUpn != null && ImpersonationService.pendingUpn !== u) {
            ImpersonationService.pendingUpn = null;
            persistPendingUpnToSession(null);
        }
        ImpersonationService.notifyOnBehalfOfListeners();
    }

    /** Set the UPN we are currently trying (so getOnBehalfOfHeader includes it for the trial fetch). Clear when fetch completes. */
    public static setPendingUpn(upn: string | null): void {
        ImpersonationService.pendingUpn = upn ? upn.trim() : null;
        persistPendingUpnToSession(ImpersonationService.pendingUpn);
        ImpersonationService.notifyOnBehalfOfListeners();
    }

    /** Call when entities/me succeeded for this UPN; enables header for all subsequent requests and adds to recent list. */
    public static setOnBehalfOfConfirmed(upn: string | null): void {
        const trimmed = upn ? upn.trim() : null;
        ImpersonationService.confirmedUpn = trimmed;
        ImpersonationService.pendingUpn = null;
        persistConfirmedUpnToSession(trimmed);
        persistPendingUpnToSession(null);
        if (trimmed) ImpersonationService.addRecentUpn(trimmed);
        ImpersonationService.notifyOnBehalfOfListeners();
    }

    /** Clear confirmed/pending after a failed entities/me so header is not sent for this UPN. */
    public static clearOnBehalfOfPending(): void {
        ImpersonationService.pendingUpn = null;
        persistPendingUpnToSession(null);
        ImpersonationService.notifyOnBehalfOfListeners();
    }

    // --- Recent UPNs (successful on-behalf-of users) ---

    public static getRecentUpns(): string[] {
        try {
            const raw = localStorage.getItem(ON_BEHALF_OF_RECENT_UPNS_STORAGE_KEY);
            if (raw == null) return [];
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
        } catch {
            return [];
        }
    }

    public static setRecentUpns(upns: string[]): void {
        const list = Array.isArray(upns) ? upns.filter((x): x is string => typeof x === 'string') : [];
        try {
            localStorage.setItem(ON_BEHALF_OF_RECENT_UPNS_STORAGE_KEY, JSON.stringify(list));
        } catch {
            // ignore
        }
        ImpersonationService.notifyRecentUpnsListeners();
    }

    public static addRecentUpn(upn: string): void {
        const trimmed = upn.trim();
        if (!trimmed) return;
        const list = ImpersonationService.getRecentUpns();
        const without = list.filter((u) => u !== trimmed);
        const next = [trimmed, ...without].slice(0, ON_BEHALF_OF_RECENT_UPNS_MAX);
        ImpersonationService.setRecentUpns(next);
    }

    /** Subscribe to recent UPNs list changes (e.g. for Footer Storage button visibility). */
    public static subscribeRecentUpns(listener: () => void): () => void {
        ImpersonationService.recentUpnsListeners.add(listener);
        return () => {
            ImpersonationService.recentUpnsListeners.delete(listener);
        };
    }

    private static notifyRecentUpnsListeners(): void {
        ImpersonationService.recentUpnsListeners.forEach((l) => l());
    }

    /** Subscribe to on-behalf-of state changes (e.g. for UserInfo border). */
    public static subscribeOnBehalfOf(listener: () => void): () => void {
        ImpersonationService.onBehalfOfListeners.add(listener);
        return () => {
            ImpersonationService.onBehalfOfListeners.delete(listener);
        };
    }

    private static notifyOnBehalfOfListeners(): void {
        ImpersonationService.onBehalfOfListeners.forEach((l) => l());
    }

    /** Last validation error (e.g. admin-on-admin, user not found). Cleared on next successful validation. */
    private static lastValidationError: string | null = null;
    private static validationErrorListeners: Set<() => void> = new Set();

    public static getLastValidationError(): string | null {
        return ImpersonationService.lastValidationError;
    }

    public static setValidationError(error: string | null): void {
        ImpersonationService.lastValidationError = error;
        ImpersonationService.validationErrorListeners.forEach((l) => l());
    }

    public static subscribeValidationError(listener: () => void): () => void {
        ImpersonationService.validationErrorListeners.add(listener);
        return () => { ImpersonationService.validationErrorListeners.delete(listener); };
    }

    /**
     * Clear the on-behalf-of session for this tab: disable, clear UPN and confirmed/pending in sessionStorage.
     * Does not clear localStorage recent UPNs.
     */
    public static clearSession(): void {
        ImpersonationService.onBehalfOfEnabled = false;
        ImpersonationService.onBehalfOfUpn = '';
        ImpersonationService.confirmedUpn = null;
        ImpersonationService.pendingUpn = null;
        ImpersonationService.lastValidationError = null;
        try {
            clearOnBehalfOfSessionKeysExceptInitMarker();
            sessionSet(ON_BEHALF_OF_ENABLED_STORAGE_KEY, 'false');
        } catch {
            // ignore
        }
        ImpersonationService.notifyOnBehalfOfListeners();
        ImpersonationService.validationErrorListeners.forEach((l) => l());
    }

    /**
     * Returns { 'x-on-behalf-of': upn } only when on-behalf-of is enabled, UPN is non-empty,
     * and the UPN is either confirmed (we got a successful entities/me for it) or pending (we're trying it this request).
     */
    public static getOnBehalfOfHeader(): Record<string, string> {
        if (!ImpersonationService.onBehalfOfEnabled) {
            return {};
        }
        const upn = ImpersonationService.onBehalfOfUpn.trim();
        if (!upn) return {};
        const confirmed = ImpersonationService.confirmedUpn;
        const pending = ImpersonationService.pendingUpn;
        if (confirmed === upn || pending === upn) {
            return { 'x-on-behalf-of': upn };
        }
        return {};
    }
}

/** Per-tab OBO state in memory before React mounts (axios / early callers). */
ImpersonationService.rehydrateFromSessionStorage();

